'use client';

import { User } from 'lucide-react';
import Image from 'next/image';
import { useSession } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
import { ButtonLogIn } from './ButtonLogIn';
import { ButtonLogOut } from './ButtonLogOut';
import { UserMenuItem } from './UserMenuItem';

export const UserMenu = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: session } = useSession();

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  if (!session) return <ButtonLogIn />;

  const name = session.user?.name || 'Default User';
  const image = session.user?.image || '/default-avatar.png';
  const email = session.user?.email || 'N/A';

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  return (
    <div className="relative z-50" ref={menuRef}>
      <Image
        src={image}
        alt={name}
        width={100}
        height={100}
        className="h-10 w-10 cursor-pointer rounded-full object-cover"
        onClick={toggleMenu}
      />

      {isMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-64 rounded-xl border border-gray-700 bg-gray-900 text-gray-100 shadow-lg z-20 py-2">
            <div className="flex items-center gap-3 p-4">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gray-700">
                <Image
                  src={image}
                  alt={name}
                  width={100}
                  height={100}
                  className="h-10 w-10 object-cover rounded-full"
                />
              </div>
              <div className="flex flex-col">
                <p className="text-base font-semibold">{name}</p>
                <p className="text-sm text-gray-400">{email}</p>
              </div>
            </div>

            <div className="space-y-1 px-2">
              <div className="my-2 border-t border-gray-700" />

              <UserMenuItem
                icon={<User className="h-5 w-5 text-gray-400" />}
                title="Your profile"
                onClick={() => setIsMenuOpen(false)}
              />

              <div className="my-2 border-t border-gray-700" />

              <ButtonLogOut />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
